import { filterData } from "../../data/filterEngine";
import { buildAggregateResult } from "../engine/aggregates";
import { groupBy } from "../engine/groupBy";
import { sortData, limitData } from "../engine/sortAndLimit";
import { computeTrend } from "../engine/trend";
import { computeCompare } from "../engine/compare";
import { computeCorrelation } from "../engine/correlation";
import { computeDriverAnalysis } from "../engine/driverAnalysis";
import { buildChartFromSpec } from "../../dashboard/chartBuilder";

const AGGREGATE_TOOLS = ["average", "sum", "maximum", "minimum", "count", "median", "mode"];

// Caps how many rows of a raw/grouped table are ever included in a result
// that gets sent to Groq, so we never leak the full dataset even when no
// terminal aggregate step ran.
const MAX_TABLE_ROWS_IN_RESULT = 25;

// Runs a multi-step plan produced by queryPlanner against the FULL dataset,
// entirely in-browser. Steps chain: transform steps (filter/groupBy/sort/
// top/bottom) narrow down `workingData`; terminal steps (average, sum,
// trend, compare, correlation, ...) compute `result` from it.
export function executePlan(plan, dataset) {

  if (!plan?.steps?.length || !Array.isArray(dataset)) {
    return { success: false, error: "No plan or dataset provided." };
  }

  let workingData = [...dataset];
  let isGrouped = false;
  let result = null;

  // Tracks what was done to get to `result`, so the final answer can say
  // "average Value where Year = 2013" instead of a context-free number that
  // looks like it covers the whole dataset.
  const appliedFilters = [];
  let appliedGroupBy = null;

  for (const step of plan.steps) {

    switch (step.tool) {

      case "filter":
        workingData = filterData(workingData, [
          {
            column: step.column,
            value: step.value,
            operator: step.operator,
            datePart: step.datePart,
          },
        ]);
        appliedFilters.push({
          column: step.column,
          operator: step.operator || "=",
          value: step.value,
          datePart: step.datePart,
        });
        break;

      case "groupBy":
        workingData = groupBy(
          workingData,
          step.dimension,
          step.measure,
          step.aggregation || "sum"
        );
        isGrouped = true;
        appliedGroupBy = { dimension: step.dimension, measure: step.measure, aggregation: step.aggregation || "sum" };
        break;

      case "sort":
        workingData = sortData(
          workingData,
          step.by || (isGrouped ? "value" : step.column),
          step.direction || "desc"
        );
        break;

      case "top":
        // Always (re)sorts by the resolved field before slicing, so "top"
        // works correctly whether or not a separate sort step preceded it.
        workingData = limitData(workingData, step.n || 5, {
          by: step.by,
          direction: "desc",
        });
        break;

      case "bottom":
        workingData = limitData(workingData, step.n || 5, {
          by: step.by,
          direction: "asc",
        });
        break;

      case "trend":
        result = computeTrend(workingData, {
          dateColumn: step.dateColumn,
          measure: step.measure,
          period: step.period || "year",
          aggregation: step.aggregation || "sum",
        });
        break;

      case "compare":
        result = computeCompare(workingData, {
          column: step.column,
          valueA: step.valueA,
          valueB: step.valueB,
          measure: step.measure,
          aggregation: step.aggregation || "sum",
          datePart: step.datePart,
        });
        break;

      case "correlation":
        result = computeCorrelation(workingData, {
          columnX: step.columnX,
          columnY: step.columnY,
        });
        break;

      case "chart":
        result = buildChartFromSpec(workingData, {
          chartType: step.chartType,
          dimension: step.dimension,
          measure: step.measure,
          aggregation: step.aggregation || "sum",
          dateColumn: step.dateColumn,
          period: step.period,
          top: step.top,
        });
        break;

      case "driver":
        result = computeDriverAnalysis(workingData, {
          measure: step.measure,
          dateColumn: step.dateColumn,
          periodA: step.periodA,
          periodB: step.periodB,
          dimensions: step.dimensions || [],
        });
        break;

      default:
        if (AGGREGATE_TOOLS.includes(step.tool)) {
          result = buildAggregateResult(step.tool, workingData, step.measure);
        }
        // Unknown tools are silently skipped rather than throwing, so one
        // bad plan step doesn't crash the whole answer.
        break;
    }
  }

  // If no terminal step produced a result (e.g. plan was just
  // filter -> groupBy -> sort -> top), the resulting table itself IS the
  // answer. Cap it so we never ship a large slice of the dataset to Groq.
  if (result === null) {
    result = {
      success: true,
      tool: "table",
      totalRows: workingData.length,
      data: workingData.slice(0, MAX_TABLE_ROWS_IN_RESULT),
      truncated: workingData.length > MAX_TABLE_ROWS_IN_RESULT,
      isGrouped,
    };
  }

  const resultWithContext = { ...result, appliedGroupBy };

  return {
    ...resultWithContext,
    appliedFilters,
    ...deriveOutputPresentation(resultWithContext, isGrouped, plan.presentation),
  };
}

// ============================================================
// OUTPUT PRESENTATION DECISION
// ============================================================
// One verified computed result can be shown as text, a chart, a table, or
// both — but NOT all three by default. The planner (which already
// understands the question's language/intent) may set plan.presentation
// to "text" | "chart" | "table" | "chart_table" when the user's wording
// makes the right format clear ("show me a chart", "give me the exact
// numbers", "just tell me"). When it doesn't set one, we fall back to a
// conservative, shape-based default — never both chart AND table unless
// there's a real reason to.
function deriveOutputPresentation(result, isGrouped, presentationHint) {

  if (!result?.success) return { outputMode: "text" };

  // A single number never benefits from a chart or table, regardless of
  // what was asked — there's nothing to compare.
  const SCALAR_TOOLS = ["average", "sum", "maximum", "minimum", "count", "median", "mode", "correlation"];
  if (SCALAR_TOOLS.includes(result.tool)) {
    return { outputMode: "text" };
  }

  // An explicit chart request ("show me a bar chart of...") always gets a
  // chart — that's literally what was asked — plus a table only if the
  // planner also detected the user wants exact values alongside it.
  if (result.tool === "chart") {
    const wantsTable = presentationHint === "chart_table";
    return {
      outputMode: wantsTable ? "chart_table" : "chart",
      chartData: result.data,
      suggestedChartType: result.chartType,
      tableData: wantsTable ? buildTableData(result) : undefined,
    };
  }

  // Grouped breakdown (e.g. "breakdown of Value by Postcode")
  if (result.tool === "table" && isGrouped && result.data?.length > 1) {

    const chartData = result.data.slice(0, 15).map(g => ({ label: String(g.group), value: g.value }));
    const tableData = buildTableData(result);

    const mode = presentationHint || (result.data.length <= 4 ? "table" : "chart_table");

    return {
      outputMode: mode,
      ...(mode.includes("chart") ? { chartData, suggestedChartType: "bar" } : {}),
      ...(mode.includes("table") ? { tableData } : {}),
    };
  }

  // A grouped result with only 0-1 rows is really a single-value answer.
  if (result.tool === "table" && isGrouped) {
    return { outputMode: "text" };
  }

  // A raw (non-grouped) row dump isn't good chart/table material on its
  // own — let the text answer carry it, capped result already guards size.
  if (result.tool === "table") {
    return { outputMode: presentationHint === "table" ? "table" : "text", tableData: presentationHint === "table" ? buildTableData(result) : undefined };
  }

  if (result.tool === "trend" && result.series?.length > 1) {

    const mode = presentationHint || "chart";

    return {
      outputMode: mode,
      ...(mode.includes("chart") ? {
        chartData: result.series.map(p => ({ label: p.period, value: p.value })),
        suggestedChartType: "line",
      } : {}),
      ...(mode.includes("table") ? {
        tableData: {
          headers: [result.period, result.measure],
          rows: result.series.map(p => ({ cells: [p.period, p.value] })),
        },
      } : {}),
    };
  }

  if (result.tool === "compare") {

    const mode = presentationHint || "chart";

    return {
      outputMode: mode,
      ...(mode.includes("chart") ? {
        chartData: [
          { label: String(result.a.label), value: result.a.value },
          { label: String(result.b.label), value: result.b.value },
        ],
        suggestedChartType: "bar",
      } : {}),
    };
  }

  if (result.tool === "driver" && result.dimensionContributions?.length) {

    const top = result.dimensionContributions[0];
    const mode = presentationHint || "chart";

    return {
      outputMode: mode,
      ...(mode.includes("chart") ? {
        chartData: top.topContributors.map(c => ({ label: c.label, value: c.change })),
        suggestedChartType: "bar",
      } : {}),
    };
  }

  return { outputMode: "text" };
}

function buildTableData(result) {

  if (result.appliedGroupBy && result.data) {
    const { dimension, measure } = result.appliedGroupBy;
    return {
      headers: [dimension, measure],
      // Each row is wrapped in an object (not a bare array) — Firestore
      // rejects an array that directly contains other arrays, but an
      // array of objects (each holding its own array field) is fine.
      rows: result.data.slice(0, 15).map(g => ({ cells: [String(g.group), g.value] })),
    };
  }

  return null;
}
